import * as TCP from 'net';
import { isError } from "util";
import { log, addon, BaseAddon, wait } from 'pao-aop';
import { SocketServer, getSocketRemoteAddress, writeDataToSocket } from '../net/socket';
import { BufferReader } from '../buffer/index';
import { BaseServer } from '../base';

/*
 * 版权: Copyright (c) 2018 red
 * 
 * 文件: proxyServer.ts
 * 创建日期: Thursday September 20th 2018
 * 作者: pao
 * 说明:
 * 1、反向代理
 * 2、外网：从Internet发来请求的端口
 * 3、内网：面向内网的端口
 * 4、代理：代理服务器的内网端口
 * 5、目标：最终提供服务的服务器
 */

/**
 * 默认字符串编码
 */
const StringEncoding = 'UTF8';

/**
 * 命令
 */
export enum Command {
    CommandConnect = 0,
    CommandProxy = 1,
    CommandEnd = 2,
}

/**
 * 等待端口的建立
 * @param outerAddress 外部地址
 */
function waitForSocketCreated(outerAddress: string, socketList: { [index: string]: TCP.Socket } | undefined) {
    /**
     * 等待目标端口创建完毕
     */
    wait(
        () => {
            if (socketList && socketList[outerAddress]) {
                return true;
            } else {
                return false;
            }
        },
        60 * 1000,
        10);
}

/**
 * Socket 缓存队列
 */
class SocketBufferQueueList {
    /**
     * 缓存队列
     */
    public bufferQueueList: { [index: string]: (Buffer | 'end' | Error)[] } = {};

    setBufferQueue(outerAddress: string) {
        this.bufferQueueList[outerAddress] = [];
    }

    pushBuffer(outerAddress: string, data: Buffer | 'end' | Error) {
        if (!this.bufferQueueList[outerAddress]) {
            this.bufferQueueList[outerAddress] = [];
        }

        this.bufferQueueList[outerAddress].push(data);
    }

    shiftBuffer(outerAddress: string) {
        if (this.bufferQueueList[outerAddress]) {
            return this.bufferQueueList[outerAddress].shift();
        }

        return undefined;
    }

    sendAll(address: string, socketList: { [index: string]: TCP.Socket }) {
        let socket = socketList[address];
        let bufferQueue = this.bufferQueueList[address];
        if (socket && bufferQueue) {
            while (bufferQueue.length > 0) {
                let data = bufferQueue.shift();

                if (typeof data === "string" && data === 'end') {
                    socket.end();
                } else if (isError(data)) {
                    socket.destroy(data as Error);
                } else {
                    log('data', `Send Data(大小=${(data as Buffer).length}, 外网地址=${address}`);
                    socket.write(data);
                }
            }
        }
    }
}

/**
 * 分发器接口
 */
export interface IDistributor {
    /**
     * 分发器检测
     * @param distributeServerID 分发服务器ID 
     * @param incommingSocket 传入的套接字
     */
    distributorCheck?(distributeServerID: string, incommingSocket: TCP.Socket): Promise<boolean>;
}

/**
 * 插件：透明分发器
 * 分发所有数据的数据分发器
 */
@addon('TransparentDistributor', '透明分发器', '分发所有数据的数据分发器')
export class TransparentDistributor extends BaseAddon implements IDistributor {
    /**
     * 分发套接字
     * @param distributeServerID 分发服务器ID 
     * @param incommingSocket 传入的套接字
     */
    async distributorCheck?(distributeServerID: string, incommingSocket: TCP.Socket): Promise<boolean> {
        return true;
    }
}

/**
 * 代理服务器信息
 */
export class ProxyServerInfo {
    /**
     * 要代理的服务器端口
     */
    serverPort?: number;
    /**
     * 服务器名称
     */
    name?: string;
    /**
     * 备注
     */
    comment?: string;
    /**
     * 分发器
     */
    distributor?: IDistributor;
}

/**
 * 分发服务器信息
 */
export class DistributeServerInfo {
    /**
     * 服务器ID
     */
    serverID?: string;
    /**
     * 服务端口
     */
    serverPort?: number;
    /**
     * 密码
     */
    password?: string;
    /**
     * 是否生效
     */
    enabled?: boolean;
    /**
     * 备注
     */
    comment?: string;
}

/**
 * 分发服务器细节
 */
class DistributeServerDetail {
    /**
     * 对应的内部主端口
     */
    innerMainSocket: TCP.Socket;
    /**
     * 代理服务器
     */
    proxyServer: ReverseProxyServer;
}

/**
 * 反向代理内网监听服务
 */
export class ReverseProxyListenServer extends SocketServer {
    /**
     * 代理服务器列表(配置)
     */
    public proxyServers?: ProxyServerInfo[];
    /**
     * 分发服务器列表(配置)
     */
    public distributeServers?: DistributeServerInfo[];
    /**
     * 代理服务实例
     */
    private proxyServerInstances?: { [port: number]: ReverseProxyServer } = {};
    /**
     * 服务明细
     */
    private distributeServerDetails?: {
        [serverID: string]: DistributeServerDetail
    } = {};

    protected onCreated?(): void {
        /**
         * 启动时开启代理服务监听
         */
        if (!this.proxyServers) {
            throw new Error("未设置代理服务器列表。");
        }

        log('proxyServer', `代理服务器数目: ${this.proxyServers.length}`);
        for (let proxyServerInfo of this.proxyServers!) {
            let proxyServer = new ReverseProxyServer("外网监听服务器", proxyServerInfo.serverPort, this.hostName);
            proxyServer.proxyServerInfo = proxyServerInfo;
            proxyServer.start!();
            this.proxyServerInstances![proxyServerInfo.serverPort] = proxyServer;
        }
    }

    /**
     * 连接建立
     * @param socket 套接字
     */
    protected onConnected?(socket: TCP.Socket): void {
        let innerAddress = getSocketRemoteAddress(socket);
        socket.on('error', error => {
            log('proxyServer', new Error(`来自${innerAddress}的内网连接发生异常: ${error}`));
        });

        socket.on('timeout', () => {
            log('proxyServer', new Error(`来自${innerAddress}的内网连接超时`));
        });

        socket.on('close', (had_error: boolean) => {
            log('proxyServer', `来自${innerAddress}的内网连接关闭`);
        });

        // 首次连接根据发过来的连接消息来判断端口是主端口还是代理端口，如果是代理端口，则创建一个代理通道
        socket.once('data', (data: Buffer) => {
            try {
                log('proxyServer', `Inner => Data(大小=${data.length})`);

                let dataReader = new BufferReader(data);
                let command = dataReader.readUInt32BE();
                if (command === Command.CommandConnect) {
                    // 读取代理服务ID
                    let distributeServerID = dataReader.readString(StringEncoding);
                    let distributeServerPassword = dataReader.readString(StringEncoding);
                    log('proxyServer', `连接请求到达(ServerID=${distributeServerID}, Password=${distributeServerPassword})`);
                    this.connectServer!(distributeServerID, distributeServerPassword, socket);
                } else if (command === Command.CommandProxy) {
                    let distributeServerID = dataReader.readString(StringEncoding);
                    let outerAddress = dataReader.readString(StringEncoding);
                    log('proxyServer', `代理请求到达(ServerID=${distributeServerID}, OuterAddress=${outerAddress})`);
                    this.distributeServers!.forEach(distributeServerInfo => {
                        if (distributeServerInfo.serverID === distributeServerID) {
                            log('proxyServer', `Inner => Proxy(外网地址=${outerAddress}`);
                            this.distributeServerDetails![distributeServerID].proxyServer!.connectInnerPort!(outerAddress, socket, distributeServerID);
                        }
                    });
                }
            } catch (error) {
                log('proxyServer', error, 'error');
            }
        });
    }

    public connectServer?(distributeServerID: string, distributeServerPassword: string, socket: TCP.Socket) {
        if (!this.distributeServers) {
            throw new Error(`未设置分布式服务器列表，服务:${distributeServerID}连接失败`);
        }

        this.distributeServers!.forEach(distributeServerInfo => {
            if (distributeServerInfo.serverID === distributeServerID && distributeServerInfo.password === distributeServerPassword) {
                let port = distributeServerInfo.serverPort!;
                log('proxyServer', `Inner => Connect(端口=${port}})`);

                let proxyServer = this.proxyServerInstances![port];
                if (!proxyServer) {
                    throw new Error(`服务${distributeServerID}要代理的端口${port}没有设置，如果需要开启某个端口，请设置proxyServers属性`);
                }
                this.distributeServerDetails![distributeServerID] = {
                    innerMainSocket: socket,
                    proxyServer: proxyServer
                };

                /**
                 * 如果是主连接，端口关闭后应当删除服务
                 */
                socket.on('close', (had_error: boolean) => {
                    let innerAddress = getSocketRemoteAddress(socket);
                    log('proxyServer', `来自${innerAddress}的内网主连接关闭`);
                    proxyServer.disconnectInnerMainPort!(distributeServerID);
                    delete this.distributeServerDetails![distributeServerID];
                });

                proxyServer.connetInnerMainPort!(distributeServerInfo, this.distributeServerDetails![distributeServerID]);
            }
        });
    }
}

/**
 * 反向代理服务
 * 反向代理服务在有IP地址公网中运行，模拟开启某个服务，并将接收到的信息转发给目标服务器
 */
export class ReverseProxyServer extends SocketServer {
    /**
     * 代理服务器信息
     */
    public proxyServerInfo?: ProxyServerInfo;
    /**
     * 外网Socket列表
     */
    public outerSocketList?: { [index: string]: TCP.Socket } = {};
    /**
     * 内网Socket列表
     */
    public innerSocketList?: { [index: string]: TCP.Socket } = {};
    /**
     * 待发送的数据缓存
     */
    public bufferList?= new SocketBufferQueueList();
    /**
     * 服务明细
     */
    public distributeServers?: {
        [serverID: string]: {
            serverInfo: DistributeServerInfo,
            serverDetail: DistributeServerDetail
        }
    } = {};

    /**
     * 连接内网主端口
     * @param innerMainSocket 内网主端口
     * @param serverInfo 服务器信息
     */
    connetInnerMainPort?(serverInfo: DistributeServerInfo, serverDetail: DistributeServerDetail) {
        this.distributeServers![serverInfo.serverID] = {
            serverInfo: serverInfo,
            serverDetail: serverDetail
        };
    }

    /**
     * 断开内网主连接端口
     * @param serverID 服务ID
     */
    disconnectInnerMainPort?(serverID: string) {
        delete this.distributeServers![serverID];
    }

    /**
     * 连接内网端口
     * @param outerAddress 外网地址
     * @param innerSocket 内部端口
     */
    connectInnerPort?(outerAddress: string, innerSocket: TCP.Socket, serverID: string) {
        innerSocket.on('error', (error: Error) => {
            log('proxyServer', new Error(`内网连接${outerAddress}异常: ${error}`));
            let outerSocket = this.outerSocketList![outerAddress];
            if (outerSocket) {
                log('proxyServer', `Error => Outer(外网地址=${outerAddress}`);
                outerSocket.destroy(error);
            }
        });

        innerSocket.on('timeout', () => {
            log('proxyServer', new Error(`内网连接${outerAddress}超时`));
        });

        innerSocket.on('close', (had_error: boolean) => {
            log('proxyServer', `内网连接${outerAddress}关闭`);
            let outerSocketList = this.outerSocketList![outerAddress];
            if (outerSocketList) {
                outerSocketList.destroy();
            }
            delete this.outerSocketList![outerAddress];
            delete this.innerSocketList![outerAddress];
        });

        // 内网数据转发到外网
        innerSocket.on('data', (data: Buffer) => {
            try {
                log('proxyServer', `Inner => Data(大小=${data.length}, 外网地址=${outerAddress})`);
                waitForSocketCreated(outerAddress, this.outerSocketList);
                let outerSocket = this.outerSocketList![outerAddress];
                if (outerSocket) {
                    log('proxyServer', `Data => Outer(大小=${data.length}, 外网地址=${outerAddress}`);
                    outerSocket.write(data);
                }
            } catch (error) {
                log('proxyServer', error, 'error');
            }
        });

        innerSocket.on('end', () => {
            try {
                log('proxyServer', `Inner => End(外网地址=${outerAddress}`);
                let outerSocket = this.outerSocketList![outerAddress];
                if (outerSocket) {
                    log('proxyServer', `End => Outer(外网地址=${outerAddress}`);
                    outerSocket.end();
                }
            } catch (error) {
                log('proxyServer', error, 'error');
            }
        });

        this.innerSocketList![outerAddress] = innerSocket;

        this.bufferList!.sendAll(outerAddress, this.innerSocketList!);
    }

    /**
     * 来自外网的连接建立
     * @param outerSocket 套接字
     */
    protected onConnected?(outerSocket: TCP.Socket): void {
        let outerAddress = getSocketRemoteAddress(outerSocket);
        this.outerSocketList![outerAddress] = outerSocket;

        // 根据转发规则建立连接
        for (let distributeServerID in this.distributeServers!) {
            if (distributeServerID) {
                let distributeServer = this.distributeServers![distributeServerID];
                // 只有生效的分发服务器才转发数据
                if (distributeServer.serverInfo.enabled) {
                    if (this.proxyServerInfo!.distributor) {
                        this.proxyServerInfo!.distributor!.distributorCheck!(distributeServerID, outerSocket).then(ok => {
                            if (ok) {
                                let distributeServerDetail = distributeServer!.serverDetail;
                                writeDataToSocket(distributeServerDetail.innerMainSocket!, (bufferWriter) => {
                                    bufferWriter.writeUInt32BE(Command.CommandConnect);
                                    bufferWriter.writeString(outerAddress, StringEncoding);
                                    log('proxyServer', `Connect => Inner(大小=${bufferWriter.bufferSize}, 外网地址=${outerAddress})`);
                                });
                            }
                        });
                    }
                }
            }
        }
        outerSocket.on('error', (error: Error) => {
            log('proxyServer', new Error(`外网连接${outerAddress}异常: ${error}`));
            this.bufferList!.pushBuffer(outerAddress, error);
            this.bufferList!.sendAll(outerAddress, this.innerSocketList!);
        });

        outerSocket.on('timeout', () => {
            log('proxyServer', new Error(`外网连接${outerAddress}超时`));
        });

        outerSocket.on('close', (had_error: boolean) => {
            log('proxyServer', `外网连接${outerAddress}关闭`);
            let innerSocket = this.innerSocketList![outerAddress];
            if (innerSocket) {
                innerSocket.destroy();
            }
            delete this.innerSocketList![outerAddress];
            delete this.outerSocketList![outerAddress];
        });

        // 外网数据转发到内网
        outerSocket.on('data', (data: Buffer) => {
            try {
                log('proxyServer', `Outer => Data(大小=${data.length}, 外网地址=${outerAddress})`);
                this.bufferList!.pushBuffer(outerAddress, data);
                waitForSocketCreated(outerAddress, this.innerSocketList);
                this.bufferList!.sendAll(outerAddress, this.innerSocketList!);
            } catch (error) {
                log('proxyServer', error, 'error');
            }
        });

        outerSocket.on('end', () => {
            try {
                log('proxyServer', `Outer => End(外网地址=${outerAddress}`);
                this.bufferList!.pushBuffer(outerAddress, 'end');
                this.bufferList!.sendAll(outerAddress, this.innerSocketList!);
            } catch (error) {
                log('proxyServer', error, 'error');
            }
        });
    }
}

/**
 * 反向代理客户端
 */
export class ReverseProxyClient extends BaseServer {
    /**
     * 代理服务端主端口
     */
    public proxyServerMainSocket?: TCP.Socket;

    /**
     * 发送到Target的缓冲区
     */
    public bufferListToTarget?= new SocketBufferQueueList();

    /**
     * 代理服务列表
     */
    public proxySocketList?: {
        [index: string]: TCP.Socket
    } = {};

    /**
     * 目标服务列表
     */
    public targetSocketList?: {
        [index: string]: TCP.Socket
    } = {};

    /**
     * 构造函数
     * @param proxyPort 要代理的端口
     * @param proxyServer 代理服务器地址
     * @param proxyServerPort 代理服务端口
     * @param targetServer 目标服务器
     * @param targetServerPort 目标端口
     */
    constructor(
        public serverID?: string,
        public password?: string,
        public proxyPort?: number,
        public proxyServer?: string,
        public proxyServerPort?: number,
        public targetServer?: string,
        public targetServerPort?: number) {
        super();
    }

    /**
     * 等待结束
     */
    async waitForExit?() {
        let intervalID: any;
        let reconnectTime = 1000 * 30;
        let that = this;
        await new Promise((resolve, reject) => {
            let reconnect = () => {
                // 每30秒检测一次客户端是否断开，如果断开则重新连接
                if (!that.proxyServerMainSocket) {
                    that.connectProxyServer!();
                }

                setTimeout(reconnect, reconnectTime);
            };
            setTimeout(reconnect, reconnectTime);
        });
        clearInterval(intervalID);
    }

    /**
     * 连接代理服务器
     */
    connectProxyServer?() {
        this.proxyServerMainSocket = new TCP.Socket();
        let proxyAddress = getSocketRemoteAddress(this.proxyServerMainSocket);

        // 数据转发
        this.proxyServerMainSocket!.on('data', (data: Buffer) => {
            try {
                log('proxyClient', `ProxyMain => Data(大小=${data.length})`);

                let bufferReader = new BufferReader(data);

                while (!bufferReader.isEnd) {
                    let command = bufferReader.readUInt32BE();

                    if (command === Command.CommandConnect) {
                        let outerAddress = bufferReader.readString(StringEncoding);
                        this.connectSockets!(outerAddress);
                    }
                }
            } catch (error) {
                log('proxyServer', error, 'error');
            }
        });

        this.proxyServerMainSocket.on('error', (error: Error) => {
            log('proxyClient', new Error(`代理连接${proxyAddress}异常: ${error}`));
        });

        this.proxyServerMainSocket.on('timeout', () => {
            log('proxyClient', new Error(`代理连接${proxyAddress}超时`));
        });

        this.proxyServerMainSocket.on('close', (had_error: boolean) => {
            log('proxyClient', `代理连接${proxyAddress}关闭`);
            this.proxyServerMainSocket = undefined;
        });

        this.proxyServerMainSocket.once('connect', () => {
            log('proxyClient', `代理服务器${getSocketRemoteAddress(this.proxyServerMainSocket!)}连接成功`);

            // 向代理服务器通发送服务ID，准备启动代理
            writeDataToSocket(this.proxyServerMainSocket!, bufferWriter => {
                bufferWriter.writeUInt32BE(Command.CommandConnect);
                bufferWriter.writeString(this.serverID!);   // serverID
                bufferWriter.writeString(this.password!);   // password
                log('proxyClient', `Connect => ProxyMain(大小=${bufferWriter.bufferSize})`);
            });
        });

        log('proxyClient', `开始连接代理服务器${this.proxyServer}:${this.proxyServerPort}`);
        this.proxyServerMainSocket.connect!(this.proxyServerPort!, this.proxyServer!);
    }

    protected onStart?() {
        this.connectProxyServer!();

        this.waitForExit!();
    }

    connectSockets?(outerAddress: string) {
        let proxySocket = new TCP.Socket();

        proxySocket.on('error', (error: Error) => {
            log('proxyClient', new Error(`代理连接异常: ${error}, 外网地址:${outerAddress}`));
            this.bufferListToTarget!.pushBuffer(outerAddress, error);
            waitForSocketCreated(outerAddress, this.targetSocketList);
            this.bufferListToTarget!.sendAll(outerAddress, this.targetSocketList!);
        });

        proxySocket.on('timeout', () => {
            log('proxyClient', new Error(`代理连接超时, 外网地址:${outerAddress}`));
        });

        proxySocket.on('close', (had_error: boolean) => {
            log('proxyClient', `代理连接关闭, 外网地址:${outerAddress}`);
            let targetSocket = this.targetSocketList![outerAddress];
            if (targetSocket) {
                targetSocket.destroy();
            }
            delete this.targetSocketList![outerAddress];
            delete this.proxySocketList![outerAddress];
        });

        // 数据转发 TargetServer -> ProxyServer
        proxySocket.on('data', (data: Buffer) => {
            try {
                log('proxyClient', `Proxy => Data(大小=${data.length}, 外网地址:${outerAddress}`);
                this.bufferListToTarget!.pushBuffer(outerAddress, data);

                waitForSocketCreated(outerAddress, this.targetSocketList);
                this.bufferListToTarget!.sendAll(outerAddress, this.targetSocketList!);
            } catch (error) {
                log('proxyServer', error, 'error');
            }
        });

        proxySocket.on('end', () => {
            try {
                log('proxyClient', `Proxy => End(外网地址=${outerAddress})`);
                this.bufferListToTarget!.pushBuffer(outerAddress, 'end');

                waitForSocketCreated(outerAddress, this.targetSocketList);
                this.bufferListToTarget!.sendAll(outerAddress, this.targetSocketList!);
            } catch (error) {
                log('proxyServer', error, 'error');
            }
        });

        proxySocket.once('connect', () => {
            try {
                log('proxyClient', `代理服务器${getSocketRemoteAddress(proxySocket)}连接成功, 外网地址:${outerAddress}`);

                // 向代理服务器通知建立代理端口
                writeDataToSocket(proxySocket, (bufferWriter) => {
                    bufferWriter.writeUInt32BE(Command.CommandProxy);
                    bufferWriter.writeString(this.serverID!);
                    bufferWriter.writeString(outerAddress);
                    log('proxyClient', `Connect => Proxy(大小=${bufferWriter.bufferSize})`);
                });

                // 代理服务器连接成功后连接目标服务器
                this.connectTargetSocket(outerAddress);

                // 代理连接建立后转发数据
                this.proxySocketList![outerAddress] = proxySocket;

                waitForSocketCreated(outerAddress, this.targetSocketList);
                this.bufferListToTarget!.sendAll(outerAddress, this.targetSocketList!);
            } catch (error) {
                log('proxyServer', error, 'error');
            }
        });

        // 连接目标服务器
        log('proxyClient', `开始连接代理服务器${this.proxyServer}:${this.proxyServerPort}, 外网地址:${outerAddress}`);
        proxySocket.connect(this.proxyServerPort!, this.proxyServer!);
    }

    /**
     * 连接目标服务器
     */
    connectTargetSocket?(outerAddress: string) {
        let targetSocket = new TCP.Socket();

        targetSocket.on('error', (error: Error) => {
            log('proxyClient', `目标连接异常: ${error}, 外网地址:${outerAddress}`);
            let proxySocket = this.proxySocketList![outerAddress];
            if (proxySocket) {
                log('proxyClient', `Error => Proxy(外网地址=${outerAddress})`);
                proxySocket.destroy(error);
            }
        });

        targetSocket.on('timeout', () => {
            log('proxyClient', `目标连接超时, 外网地址:${outerAddress}`);
        });

        targetSocket.on('close', (had_error: boolean) => {
            log('proxyClient', `目标连接关闭(外网地址:${outerAddress})`);
            let proxySocket = this.proxySocketList![outerAddress];
            if (proxySocket) {
                proxySocket.destroy();
            }
            delete this.proxySocketList![outerAddress];
            delete this.targetSocketList![outerAddress];
        });

        // 数据转发 TargetServer -> ProxyServer
        targetSocket.on('data', (data: Buffer) => {
            log('proxyClient', `Target => Data(大小=${data.length}, 外网地址=${outerAddress})`);
            let proxySocket = this.proxySocketList![outerAddress];

            log('proxyClient', `Data => Proxy(大小=${data.length}, 外网地址=${outerAddress})`);
            waitForSocketCreated(outerAddress, this.proxySocketList);
            proxySocket.write(data);
        });

        targetSocket.on('end', () => {
            log('proxyClient', `Target => End(外网地址=${outerAddress})`);
            let proxySocket = this.proxySocketList![outerAddress];
            if (proxySocket) {
                log('proxyClient', `End => Proxy(外网地址=${outerAddress})`);
                proxySocket.end();
            }
        });

        targetSocket.once('connect', () => {
            try {
                log('proxyClient', `目标服务器${getSocketRemoteAddress(targetSocket)}连接成功, 外网地址:${outerAddress}`);

                this.targetSocketList![outerAddress] = targetSocket;
            } catch (error) {
                log('proxyServer', error, 'error');
            }
        });

        // 连接目标服务器
        log('proxyClient', `开始连接目标服务器${this.targetServer!}:${this.targetServerPort!}, 外网地址:${outerAddress}`);
        targetSocket.connect(this.targetServerPort!, this.targetServer!);
    }
}